import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "lib/api";
import { perfilKeys } from "./keys";

const PerfilSettingsContext = React.createContext(null);

// optional LS (not required now, but keeps pattern consistent for future)
const LS_KEY = "perfil.settings";
function loadLS() {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || "{}"); } catch { return {}; }
}
function saveLS(obj) {
  localStorage.setItem(LS_KEY, JSON.stringify(obj));
}

export function PerfilSettingsProvider({ children }) {
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: perfilKeys.context(),
    queryFn: () => api("/perfil/context"),
    staleTime: 60_000,
  });

  // reserved for future (e.g. selected tab, selected registration mode, etc.)
  const [state, setState] = React.useState(() => loadLS());

  const value = React.useMemo(() => {
    if (!q.data?.usuario || !q.data?.familia) {
      return { ready: false, loading: q.isLoading, error: q.error };
    }

    const usuario = q.data.usuario;
    const familia = q.data.familia;

    const actions = {
      // future safe place to add: setSelectedKidId, setRegistroMode, etc.
      setState: (next) => { setState(next); saveLS(next); },
      invalidateContext: () => qc.invalidateQueries({ queryKey: perfilKeys.context() }),
      invalidateHijos: () => qc.invalidateQueries({ queryKey: perfilKeys.hijos() }),
      refetchContext: () => q.refetch(),
    };

    return {
      ready: true,
      loading: false,
      error: null,

      usuario,
      familia,
      familiaId: usuario.familiaId || null,

      // keep state slot for future
      state,
      ...actions,
    };
  }, [q.data, q.isLoading, q.error, qc, state, q.refetch]);

  return (
    <PerfilSettingsContext.Provider value={value}>
      {children}
    </PerfilSettingsContext.Provider>
  );
}

export function usePerfilSettings() {
  const ctx = React.useContext(PerfilSettingsContext);
  if (!ctx) throw new Error("usePerfilSettings must be used inside PerfilSettingsProvider");
  return ctx;
}
